import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing RTQ Claims Experiences with add/delete records and auto-save.
 *
 * Usage:
 *   <div data-controller="rtq-claims-experiences"
 *        data-rtq-claims-experiences-create-url-value="/path/to/create"
 *        data-rtq-claims-experiences-max-records-value="3">
 */
export default class extends Controller {
  static targets = ["record", "addButton", "status"]

  static values = {
    createUrl: String,
    maxRecords: { type: Number, default: 3 },
    debounce: { type: Number, default: 500 }
  }

  connect() {
    this.debounceTimers = {}
    this.updateAddButtonVisibility()
  }

  disconnect() {
    Object.values(this.debounceTimers).forEach(timer => clearTimeout(timer))
  }

  updateAddButtonVisibility() {
    if (this.hasAddButtonTarget) {
      const recordCount = this.recordTargets.length
      this.addButtonTarget.classList.toggle("hidden", recordCount >= this.maxRecordsValue)
    }
  }

  async addRecord(event) {
    event.preventDefault()

    const recordCount = this.recordTargets.length
    if (recordCount >= this.maxRecordsValue) return

    this.showStatus("Adding...")

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_claims_experience: {} })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        window.location.reload()
      } else {
        this.showStatus("Error adding", true)
      }
    } catch (error) {
      console.error("Error adding claims experience:", error)
      this.showStatus("Network error", true)
    }
  }

  async deleteRecord(event) {
    event.preventDefault()

    if (!confirm("Are you sure you want to delete this claims experience period?")) return

    const button = event.currentTarget
    const url = button.dataset.url

    this.showStatus("Deleting...")

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        window.location.reload()
      } else {
        this.showStatus("Error deleting", true)
      }
    } catch (error) {
      console.error("Error deleting claims experience:", error)
      this.showStatus("Network error", true)
    }
  }

  fieldChanged(event) {
    const input = event.target
    const url = input.dataset.url
    const field = input.dataset.field

    if (!url || !field) return

    // Clear existing timer for this input
    const timerId = `${url}-${field}`
    if (this.debounceTimers[timerId]) {
      clearTimeout(this.debounceTimers[timerId])
    }

    this.showStatus("Saving...")

    this.debounceTimers[timerId] = setTimeout(() => {
      this.saveField(url, field, input.value)
    }, this.debounceValue)
  }

  async saveField(url, field, value) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_claims_experience: { [field]: value } })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        this.showStatus("Saved", false, 2500)
      } else {
        this.showStatus("Error saving", true)
      }
    } catch (error) {
      console.error("Error saving field:", error)
      this.showStatus("Network error", true)
    }
  }

  showStatus(message, isError = false, autoClearMs = 0) {
    if (!this.hasStatusTarget) return

    const statusClass = isError ? "text-red-600" : "text-gray-400"
    this.statusTarget.innerHTML = `<span class="${statusClass}">${message}</span>`

    if (autoClearMs > 0) {
      setTimeout(() => {
        this.statusTarget.innerHTML = ""
      }, autoClearMs)
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
